import { NgZone } from '@angular/core';

const INTERVAL_MS = {
  afterFailedMatch: 10_000,
  deferWhileBlocked: 400
} as const;

export interface FaceVerifyCadenceSink {
  isDestroyed(): boolean;
  isScannerVisible(): boolean;
  isVerifyInFlight(): boolean;
  isVideoFrameReady(): boolean;
  fireScheduledVerify(): void;
}

/** Schedules post-failure verify retries; success timing is liveness-based in `FaceScanComponent`. */
export class FaceVerifyCadence {
  private lastApiSettledAt = 0;
  private pendingFailureRetry = false;
  private timer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private readonly zone: NgZone,
    private readonly sink: FaceVerifyCadenceSink
  ) {}

  clearSchedule(): void {
    this.clearTimer();
  }

  get awaitingFirstResponse(): boolean {
    return this.lastApiSettledAt === 0;
  }

  notifyVerifyResponse(matched: boolean): void {
    this.lastApiSettledAt = Date.now();
    this.clearTimer();
    this.pendingFailureRetry = !matched;
    if (!matched) this.arm(INTERVAL_MS.afterFailedMatch);
  }

  onScannerShownAgain(): void {
    if (!this.pendingFailureRetry || this.lastApiSettledAt === 0) return;
    const remaining = INTERVAL_MS.afterFailedMatch - (Date.now() - this.lastApiSettledAt);
    this.arm(remaining);
  }

  /**
   * No-face / camera-off lock happens without an HTTP verify. Still arm the same post-failure delay
   * so the next verify runs after `afterFailedMatch` (does not reset an already-running retry timer).
   */
  notifyEnvironmentalLockout(): void {
    if (this.timer != null || this.lastApiSettledAt === 0) return;
    this.lastApiSettledAt = Date.now();
    this.pendingFailureRetry = true;
    this.arm(INTERVAL_MS.afterFailedMatch);
  }

  private arm(delayMs: number): void {
    this.clearTimer();
    const flush = () => this.tryFlush();
    if (delayMs <= 0) {
      this.zone.run(flush);
      return;
    }
    this.timer = setTimeout(() => {
      this.timer = null;
      if (this.sink.isDestroyed() || !this.sink.isScannerVisible()) return;
      this.zone.run(flush);
    }, delayMs);
  }

  private tryFlush(): void {
    if (this.sink.isDestroyed() || !this.sink.isScannerVisible()) return;
    if (this.sink.isVerifyInFlight() || !this.sink.isVideoFrameReady()) {
      this.deferWhileBlocked();
      return;
    }
    this.sink.fireScheduledVerify();
  }

  private deferWhileBlocked(): void {
    this.clearTimer();
    this.timer = setTimeout(() => {
      this.timer = null;
      if (this.sink.isDestroyed() || !this.sink.isScannerVisible()) return;
      this.zone.run(() => this.tryFlush());
    }, INTERVAL_MS.deferWhileBlocked);
  }

  private clearTimer(): void {
    if (this.timer == null) return;
    clearTimeout(this.timer);
    this.timer = null;
  }
}
